import { useState, useCallback } from 'react';

/**
 * Custom hook to calculate dropdown position and max height
 * based on available viewport space
 * 
 * @param {React.RefObject} anchorRef - Reference to the anchor element
 * @param {number} preferredHeight - Preferred dropdown height
 * @returns {Object} { position: 'top' | 'bottom', maxHeight: number, calculatePosition: function }
 */
const useDropdownPosition = (anchorRef, preferredHeight = 304) => {
  const [position, setPosition] = useState('bottom');
  const [maxHeight, setMaxHeight] = useState(preferredHeight);

  const calculatePosition = useCallback(() => {
    if (!anchorRef.current) {
      setPosition('bottom');
      setMaxHeight(preferredHeight);
      return;
    }

    const rect = anchorRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    let calculatedPosition = 'bottom';
    let calculatedMaxHeight = preferredHeight;

    if (spaceBelow < preferredHeight && spaceAbove > spaceBelow) {
      calculatedPosition = 'top';
      calculatedMaxHeight = Math.min(preferredHeight, spaceAbove - 20);
    } else {
      calculatedPosition = 'bottom';
      calculatedMaxHeight = Math.min(preferredHeight, spaceBelow - 20);
    }

    setPosition(calculatedPosition);
    setMaxHeight(calculatedMaxHeight);
  }, [anchorRef, preferredHeight]);

  return { position, maxHeight, calculatePosition };
};

export default useDropdownPosition;
